import { RichTextField } from '@prismicio/types'

const FORBIDDEN_PATTERN =
  /\b(this|window|document|globalThis|Function|eval|constructor|prototype|__proto__|new)\b|=>|;|`/i
const ALLOWED_CHARS_PATTERN = /^[\w\s.$()[\]!<>=&|+\-*/%:'",?]+$/
const IDENTIFIER_PATTERN = /\b[a-zA-Z_][\w.]*/g
const RESERVED_WORDS = new Set([
  'true',
  'false',
  'null',
  'undefined',
  'NaN',
  'Infinity',
])

const isValidExpression = (expression: string): boolean => {
  if (!expression || expression.length > 300) return false
  if (FORBIDDEN_PATTERN.test(expression)) return false
  return ALLOWED_CHARS_PATTERN.test(expression)
}

const hasOnlyAllowedIdentifiers = (
  expression: string,
  context: Record<string, unknown>
): boolean => {
  const rootKeys = new Set(Object.keys(context))
  const tokens: string[] = expression.match(IDENTIFIER_PATTERN) || []

  return tokens.every((token) => {
    if (RESERVED_WORDS.has(token)) return true
    const root = token.split('.')[0]
    return rootKeys.has(root)
  })
}

export const getConditionString = (
  condition: string | RichTextField | undefined
): string => {
  if (!condition) return ''
  if (typeof condition === 'string') return condition.trim()

  if (Array.isArray(condition)) {
    return condition
      .map((block) => ('text' in block ? block.text : ''))
      .join(' ')
      .trim()
  }

  return ''
}

export const evaluateCondition = (
  condition: string | RichTextField | undefined,
  context: Record<string, unknown>
): boolean => {
  const expression = getConditionString(condition)
  if (!expression) return false
  if (!isValidExpression(expression)) return false
  if (!hasOnlyAllowedIdentifiers(expression, context)) return false

  try {
    const keys = Object.keys(context)
    const values = keys.map((key) => context[key])
    const evaluator = new Function(
      ...keys,
      `"use strict"; return Boolean(${expression});`
    )
    return Boolean(evaluator(...values))
  } catch (error) {
    return false
  }
}
